/**
 * Convert a dash, dot, underscore, space separated string to camelCase or PascalCase.
 *
 * @param {string} string string to convert
 * @param {object} options
 * @example
 * toCamelCase('foo-bar') // fooBar
 * toCamelCase('foo_bar') // fooBar
 * toCamelCase('Foo-Bar') // fooBar
 * toCamelCase('--Foo-Bar', { pascalCase: true }) // FooBar
 * toCamelCase('foo bar'); // fooBar
 *
 * @return {string} converted string
 */
export const toCamelCase = (string, options) => {
    if (!string || !string.length) {
        return '';
    }

    const opts = { pascalCase: false, ...options };

    if (string.length === 1) {
        return opts.pascalCase ? string.toUpperCase() : string.toLowerCase();
    }

    const postProcess = (x) => (opts.pascalCase ? x.charAt(0).toUpperCase() + x.slice(1) : x);

    return postProcess(
        string
            .replace(/^[_.\- ]+/, '')
            .toLowerCase()
            .replace(/[_.\- ]+(\w|$)/g, (m, p1) => p1.toUpperCase())
    );
};

/**
 * Convert camelcase to dash (hyphen).
 *
 * @param {string} string string to convert
 * @return {string} concerted string
 */
export const camelCaseToDash = (string) => {
    if (!string || !string.length) {
        return '';
    }

    return string
        .replace(/[^a-zA-Z0-9]+/g, '-')
        .replace(/([A-Z]+)([A-Z][a-z])/g, '$1-$2')
        .replace(/([a-z])([A-Z])/g, '$1-$2')
        .replace(/([0-9])([^0-9])/g, '$1-$2')
        .replace(/([^0-9])([0-9])/g, '$1-$2')
        .replace(/-+/g, '-')
        .toLowerCase();
};

/**
 * `Math.random()` should be unique because of its seeding algorithm.
 * Convert it to base 36 (numbers + letters), and grab the first 9 characters
 * after the decimal.
 *
 * @param {number} [length=9] - characters length
 * @see {@link https://gist.github.com/gordonbrander/2230317}
 *
 * @returns {string} a unique ID
 */
export const uniqueId = (length = 9) =>
    Math.random()
        .toString(36)
        .substr(2, length);

/**
 * Checks if a string or an object it's a JSON.
 *
 * @param {string|object} item - item to check
 * @returns {boolean}
 */
export const isJSON = (item) => {
    const string = typeof item !== 'string' ? JSON.stringify(item) : item;

    try {
        const object = JSON.parse(string);

        return object && typeof object === 'object' && object.constructor === Object;
    } catch (e) {
        return false;
    }
};
